import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import adminApi from '../api/client';

// ── Types ────────────────────────────────────────────────────────────
interface LoginResponse {
  pre_auth_token: string;
  message: string;
}

interface Verify2FAResponse {
  admin_name: string;
  session_hours: number;
}

// ── Component ────────────────────────────────────────────────────────
const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const reauthenticate = new URLSearchParams(location.search).get('reauth') === '1' && isAuthenticated;
  const requestedReturn = new URLSearchParams(location.search).get('returnTo') || '/dashboard';
  const returnTo = /^\/[a-z][a-z0-9/-]*$/.test(requestedReturn) && !requestedReturn.startsWith('/login')
    ? requestedReturn : '/dashboard';
  const [rememberEnabled, setRememberEnabled] = useState(false);
  const [rememberBrowser, setRememberBrowser] = useState(false);

  useEffect(() => {
    let active = true;
    adminApi.get<{ remember_browser_enabled: boolean }>('/admin/login-options')
      .then(response => { if (active) setRememberEnabled(response.data.remember_browser_enabled === true); })
      .catch(() => { if (active) setRememberEnabled(false); });
    return () => { active = false; };
  }, []);

  // Step tracking
  const [step, setStep] = useState<1 | 2>(1);

  // Step 1 fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Step 2 fields
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [preAuthToken, setPreAuthToken] = useState('');

  // Shared state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Step 1: Email + Password Login ───────────────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await adminApi.post<LoginResponse>('/admin/login', {
        email,
        password,
        remember_browser: rememberEnabled && rememberBrowser,
        reauthenticate,
      });
      setPreAuthToken(res.data.pre_auth_token);
      setPassword('');
      setStep(2);
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: OTP Verification ─────────────────────────────────────
  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits.');
      setLoading(false);
      return;
    }

    try {
      const res = await adminApi.post<Verify2FAResponse>('/admin/verify-2fa', {
        pre_auth_token: preAuthToken,
        otp: otpCode,
      });
      // The backend sets the httpOnly admin_token cookie in the Set-Cookie
      // response header — browser handles it automatically. We just pass
      // admin_name to AuthContext for display purposes.
      login(res.data.admin_name);
      setOtp(['', '', '', '', '', '']);
      setPreAuthToken('');
      navigate(reauthenticate ? returnTo : '/dashboard', { replace: true });
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Verification failed. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  // ── OTP Input Handlers ───────────────────────────────────────────
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // digits only

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // single digit
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const next = document.getElementById(`otp-${index + 1}`);
      next?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prev = document.getElementById(`otp-${index - 1}`);
      prev?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pasted[i] || '';
      }
      setOtp(newOtp);
      // Focus the last filled input or the next empty one
      const focusIdx = Math.min(pasted.length, 5);
      document.getElementById(`otp-${focusIdx}`)?.focus();
    }
  };

  // ── Render ───────────────────────────────────────────────────────
  if (isLoading) {
    return <div role="status" className="min-h-screen bg-cream flex items-center justify-center">Checking your admin session…</div>;
  }
  if (isAuthenticated && !reauthenticate) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-accent/5 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md animate-fade-in">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-primary/5 border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-primary px-8 py-8 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent/30" />
            <div className="relative">
              <span className="text-4xl mb-3 block">📚</span>
              <h1 className="text-white text-2xl font-bold tracking-tight">
                Readora Admin
              </h1>
              <p className="text-white/60 text-sm mt-1.5 font-medium">
                {step === 1 ? 'Sign in to your admin account' : 'Two-factor authentication'}
              </p>
            </div>
          </div>

          {/* Step indicator */}
          <div className="flex items-center px-8 pt-6 gap-3">
            <div className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${step >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-400'
                }`}>
                {step > 1 ? '✓' : '1'}
              </div>
              <span className={`text-xs font-medium ${step >= 1 ? 'text-primary' : 'text-gray-400'}`}>
                Credentials
              </span>
            </div>
            <div className={`flex-1 h-0.5 rounded-full transition-colors ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`} />
            <div className="flex items-center gap-2 flex-1 justify-end">
              <span className={`text-xs font-medium ${step >= 2 ? 'text-primary' : 'text-gray-400'}`}>
                Verify
              </span>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${step >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-400'
                }`}>
                2
              </div>
            </div>
          </div>

          {/* Form body */}
          <div className="px-8 py-6">
            {/* Error Alert */}
            {error && (
              <div className="mb-5 p-3.5 rounded-xl bg-coral/5 border border-coral/20 flex items-start gap-3 animate-slide-up">
                <svg className="w-5 h-5 text-coral shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-coral text-sm font-medium">{error}</p>
              </div>
            )}

            {/* ── Step 1: Login Form ─────────────────────────────── */}
            {step === 1 && (
              <form onSubmit={handleLogin} className="space-y-5 animate-fade-in">
                {reauthenticate && <p role="status" className="text-sm text-gray-600">Verify your password and two-factor code to continue with this sensitive action. After verification, review and submit the action again.</p>}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="admin@readora.lk"
                      className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 bg-cream/50
                        text-gray-800 text-sm placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                        transition-all duration-200"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full pl-12 pr-12 py-3 rounded-xl border border-gray-200 bg-cream/50
                        text-gray-800 text-sm placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                        transition-all duration-200"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {rememberEnabled && !reauthenticate && (
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={rememberBrowser} disabled={loading}
                        onChange={event => setRememberBrowser(event.target.checked)} />
                      Remember this browser for 2 days
                    </label>
                    <p className="mt-1 text-xs text-gray-500">Use only on your personal computer. Logout ends this session.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  id="login-submit"
                  className="
                    w-full py-3.5 rounded-xl text-white text-sm font-bold
                    bg-gradient-to-r from-primary to-accent
                    hover:shadow-lg hover:shadow-primary/25 hover:-translate-y-0.5
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none
                    transition-all duration-200
                    active:scale-[0.98]
                  "
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Signing in...
                    </span>
                  ) : (
                    'Continue'
                  )}
                </button>
              </form>
            )}

            {/* ── Step 2: OTP Verification ───────────────────────── */}
            {step === 2 && (
              <form onSubmit={handleVerify2FA} className="space-y-6 animate-fade-in">
                <div className="text-center mb-2">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/5 mb-4">
                    <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Enter the 6-digit code sent to<br />
                    <span className="font-semibold text-gray-700">{email}</span>
                  </p>
                </div>

                {/* OTP Inputs */}
                <div className="flex justify-center gap-2.5" onPaste={handleOtpPaste}>
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      id={`otp-${idx}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      className={`
                        w-12 h-14 text-center text-xl font-bold rounded-xl border-2
                        bg-cream/50 text-gray-800
                        focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                        transition-all duration-200
                        ${digit ? 'border-primary/30 bg-primary/5' : 'border-gray-200'}
                      `}
                    />
                  ))}
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.some((d) => !d)}
                  id="verify-otp-submit"
                  className="
                    w-full py-3.5 rounded-xl text-white text-sm font-bold
                    bg-gradient-to-r from-primary to-accent
                    hover:shadow-lg hover:shadow-primary/25 hover:-translate-y-0.5
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none
                    transition-all duration-200
                    active:scale-[0.98]
                  "
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    'Verify & Sign In'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setOtp(['', '', '', '', '', '']);
                    setError('');
                  }}
                  className="w-full text-center text-sm text-gray-400 hover:text-primary transition-colors font-medium"
                >
                  ← Back to login
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Secure admin access • Readora © 2026
        </p>
      </div>
    </div>
  );
};

export default Login;
